import React from "react";

import { HistoryFillOut } from "features/CenterContent/RoleContent/VehicleChecklist/schema/responseSchema";
import {
  formatDateTime,
  getChecklistText,
  getCompanyName,
} from "features/CenterContent/RoleContent/VehicleChecklist/utils";
import { Stack, Text } from "shared/components/Spor";

type FieldDefinition = Partial<{
  [T in keyof HistoryFillOut]: {
    label: string;
    valueFormatter: (value: any) => string;
  };
}>;

const CHANGEABLE_FIELDS: FieldDefinition = {
  rollingStock: {
    label: "Kjøretøy",
    valueFormatter: String,
  },
  executedAt: {
    label: "Tidspunkt",
    valueFormatter: (value) => formatDateTime(value.toString()),
  },
  checklistType: {
    label: "Kvitteringstype",
    valueFormatter: getChecklistText,
  },
  executorName: {
    label: "Kvittert av",
    valueFormatter: String,
  },
  companyId: {
    label: "Selskap",
    valueFormatter: getCompanyName,
  },
};

function hasChanges(
  previous: Record<string, any>,
  current: Record<string, any>,
) {
  return Object.entries(CHANGEABLE_FIELDS).some(
    ([key, { valueFormatter }]) =>
      valueFormatter(previous[key]) !== valueFormatter(current[key]),
  );
}

type ChangeItemProps<T extends keyof HistoryFillOut> = FieldDefinition[T] & {
  previous: HistoryFillOut[T];
  current: HistoryFillOut[T];
};

// Component to display individual changes in the checklist history
const ChangeItem = <T extends keyof HistoryFillOut>({
  label,
  previous,
  current,
  valueFormatter,
}: ChangeItemProps<T>) => {
  const prevValue = valueFormatter(previous);
  const currValue = valueFormatter(current);

  if (prevValue === currValue) return null;

  return (
    <Text>
      {label} fra{" "}
      <Text as="span" fontWeight="bold">
        {prevValue}
      </Text>{" "}
      til{" "}
      <Text as="span" fontWeight="bold">
        {currValue}
      </Text>
    </Text>
  );
};

type ChecklistChangesProps = {
  currentEntry: HistoryFillOut;
  previousEntry: HistoryFillOut;
};

const ChecklistChanges: React.FC<ChecklistChangesProps> = ({
  currentEntry,
  previousEntry,
}) =>
  hasChanges(currentEntry, previousEntry) ? (
    <>
      <Text fontWeight="bold" gridColumn="2">
        Endret:
      </Text>

      <Stack gridColumn="3 / 7" gap="3px">
        {Object.entries(CHANGEABLE_FIELDS).map(
          ([key, { label, valueFormatter }]) => (
            <ChangeItem
              key={key}
              label={label}
              current={currentEntry[key as keyof HistoryFillOut]}
              previous={previousEntry[key as keyof HistoryFillOut]}
              valueFormatter={valueFormatter}
            />
          ),
        )}
      </Stack>
    </>
  ) : null;

export default ChecklistChanges;
